// Cross-venue My Trades display sorting (#127). One comparator orders every
// list on the page — the Kalshi audit/trader tables (source rows), the
// merged card grid, and the unified all-venues table (TradeCardVMs) — so the
// user-selected sort means the same thing everywhere.
//
// Modeled on PositionTracker's orderSort.ts: rows are reduced to a small set
// of normalized PRESENTATION fields, the comparator sees only those, and the
// final tie-break is a stable row id so equal-keyed rows never reshuffle
// across refetches.
//
// Unit caveat: `pnl` is ordinal only. Each sortTrades call uses exactly one
// extractor, so a list is uniformly cents (Kalshi source rows) or dollars
// (TradeCardVMs) — magnitudes are never compared across extractors.

import type { TradeCardVM } from '../../components/cards/TradeCard';

export type TradeSortKey = 'date' | 'pnl';
export type TradeSortDir = 'asc' | 'desc';

export interface TradeSortState {
  key: TradeSortKey;
  dir: TradeSortDir;
}

export const DEFAULT_TRADE_SORT: TradeSortState = { key: 'date', dir: 'desc' };

/** Header-click cycle: desc → asc → back to the default sort. */
export function nextTradeSort(
  prev: TradeSortState,
  clicked: TradeSortKey,
): TradeSortState {
  if (clicked !== prev.key) return { key: clicked, dir: 'desc' };
  if (prev.dir === 'desc') return { key: clicked, dir: 'asc' };
  return DEFAULT_TRADE_SORT;
}

/** Normalized sort fields — the only thing the comparator reads. Null means
 *  "unavailable": those rows sort to the tail in BOTH directions rather than
 *  being coerced to zero, -Infinity, or the epoch. */
export interface TradeSortFields {
  /** Stable row identity (row.key / vm.key) — the final tie-break. */
  id: string;
  ticker: string;
  closedAtMs: number | null;
  pnl: number | null;
}

export type TradeSortFieldsOf<T> = (row: T) => TradeSortFields;

/** Covers both Kalshi table row shapes (DisplayRow and TraderRow) — they
 *  share this structural subset, which is what collapses the two old
 *  near-duplicate sort functions into one comparator. pnl in cents. */
export const kalshiRowSortFields: TradeSortFieldsOf<{
  key: string;
  ticker: string;
  closedAt: number;
  netPnlCents: number | null;
}> = (r) => ({
  id: r.key,
  ticker: r.ticker,
  closedAtMs: r.closedAt > 0 ? r.closedAt : null,
  pnl: r.netPnlCents,
});

/** TradeCardVM extractor — both venues. pnl in dollars. */
export const tradeVMSortFields: TradeSortFieldsOf<TradeCardVM> = (vm) => ({
  id: vm.key,
  ticker: vm.ticker,
  closedAtMs: vm.closedAtMs > 0 ? vm.closedAtMs : null,
  pnl: vm.netPnlDollars,
});

/** Null-to-tail primary comparison. Returns 0 when both are null (fall to
 *  the tie chain) — a shared "unavailable" bucket, not a spurious order. */
function comparePrimary(
  a: number | null,
  b: number | null,
  dirSign: number,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return (a - b) * dirSign;
}

export function makeTradeComparator<T>(
  sort: TradeSortState,
  fields: TradeSortFieldsOf<T>,
): (a: T, b: T) => number {
  const dirSign = sort.dir === 'asc' ? 1 : -1;
  return (rowA, rowB) => {
    const a = fields(rowA);
    const b = fields(rowB);
    const primary =
      sort.key === 'pnl'
        ? comparePrimary(a.pnl, b.pnl, dirSign)
        : comparePrimary(a.closedAtMs, b.closedAtMs, dirSign);
    if (primary !== 0) return primary;
    // Direction-independent tie chain: newest first, then ticker, then the
    // stable id — so equal-keyed rows keep one canonical order.
    if (a.closedAtMs != null && b.closedAtMs != null && a.closedAtMs !== b.closedAtMs) {
      return b.closedAtMs - a.closedAtMs;
    }
    if ((a.closedAtMs == null) !== (b.closedAtMs == null)) {
      return a.closedAtMs == null ? 1 : -1;
    }
    const byTicker = a.ticker.localeCompare(b.ticker);
    if (byTicker !== 0) return byTicker;
    return a.id.localeCompare(b.id);
  };
}

/** Non-mutating sort. Output elements are the same objects that went in —
 *  per-venue source rows stay traceable through the sort. */
export function sortTrades<T>(
  rows: readonly T[],
  sort: TradeSortState,
  fields: TradeSortFieldsOf<T>,
): T[] {
  return [...rows].sort(makeTradeComparator(sort, fields));
}
